import { formatDateTime } from '@/services/functions';
import i18n from '@/lang/i18n';
import type DeliveryPlan from '@/types/DeliveryPlan';

const LOCALE_OPTS: Intl.NumberFormatOptions = { maximumFractionDigits: 5 };

class MileageCorrectionService {
  public static formatMileage(value: number): string {
    return value.toLocaleString(undefined, LOCALE_OPTS);
  }

  public static roundMileage(value: number): number {
    return Math.round(value * 1e5) / 1e5;
  }

  public static toDateTime(datetime: string | null): string {
    if (datetime === null) {
      return '-';
    }
    return formatDateTime(new Date(datetime));
  }

  public static isCorrected(item: DeliveryPlan, correctedMeters: Map<string, number>): boolean {
    return correctedMeters.has(item.delivery_plan_id);
  }

  public static getEffectiveOutOfServiceMeter(item: DeliveryPlan, correctedMeters: Map<string, number>): number | null {
    if (MileageCorrectionService.isCorrected(item, correctedMeters)) {
      return correctedMeters.get(item.delivery_plan_id)!;
    }
    return item.out_of_service_meter != null ? Number(item.out_of_service_meter) : null;
  }

  public static getPrevReturnMeter(item: DeliveryPlan, sortedTrips: DeliveryPlan[], correctedMeters: Map<string, number>): number | null {
    const index = sortedTrips.findIndex(op => op.delivery_plan_id === item.delivery_plan_id);
    if (index <= 0) {
      return null;
    }
    const prev = sortedTrips[index - 1];
    const prevOutOfServiceMeter = MileageCorrectionService.getEffectiveOutOfServiceMeter(prev, correctedMeters);
    if (prevOutOfServiceMeter === null) {
      return null;
    }
    return MileageCorrectionService.roundMileage(prevOutOfServiceMeter + Number(prev.total_mileage ?? 0));
  }

  public static getDiff(item: DeliveryPlan, correctedMeters: Map<string, number>, sortedTrips: DeliveryPlan[]): string {
    if (MileageCorrectionService.isCorrected(item, correctedMeters)) {
      const corrected = correctedMeters.get(item.delivery_plan_id)!;
      const original = Number(item.out_of_service_meter ?? 0);
      const diff = corrected - original;
      return (diff >= 0 ? '+' : '') + MileageCorrectionService.formatMileage(diff);
    }

    const prevReturnMeter = MileageCorrectionService.getPrevReturnMeter(item, sortedTrips, correctedMeters);
    if (prevReturnMeter === null || item.out_of_service_meter == null) {
      return '-';
    }
    const diff = MileageCorrectionService.roundMileage(Number(item.out_of_service_meter) - prevReturnMeter);
    return (diff >= 0 ? '+' : '') + MileageCorrectionService.formatMileage(diff);
  }

  public static displayOutOfServiceMeter(item: DeliveryPlan, correctedMeters: Map<string, number>): string {
    const meter = MileageCorrectionService.getEffectiveOutOfServiceMeter(item, correctedMeters);
    return meter != null ? MileageCorrectionService.formatMileage(meter) : '-';
  }

  public static displayTotalMileage(item: DeliveryPlan): string {
    return item.total_mileage != null ? MileageCorrectionService.formatMileage(Number(item.total_mileage)) : '-';
  }

  public static displayReturnMeter(item: DeliveryPlan, correctedMeters: Map<string, number>): string {
    const outOfServiceMeter = MileageCorrectionService.getEffectiveOutOfServiceMeter(item, correctedMeters);
    return outOfServiceMeter != null ? MileageCorrectionService.formatMileage(outOfServiceMeter + Number(item.total_mileage ?? 0)) : '-';
  }

  public static validateOutOfServiceMeter(raw: string): string {
    if (raw === '') {
      return i18n.global.t('pages.mileageCorrection.validation.required');
    }
    const value = parseFloat(raw);
    if (value < 0) {
      return i18n.global.t('pages.mileageCorrection.validation.negative');
    }
    const decimalPart = raw.includes('.') ? raw.split('.')[1] : '';
    if (decimalPart.length > 5) {
      return i18n.global.t('pages.mileageCorrection.validation.decimalDigits');
    }
    return '';
  }
}

export default MileageCorrectionService;
